import React, { Suspense, useDeferredValue, useEffect, useState } from 'react';
import { Service, AddOn } from '@/types';
import { Plus, Loader2 } from 'lucide-react';
import { Input } from '@shared/components/ui/Input';
import { useServicesAll, useQuestionnairesAll, DataErrorBoundary } from '@features/admin/data';
import { ServiceEditor } from './ServiceEditor';
import { AddOnEditor } from './AddOnEditor';
import { ServicesList } from './ServicesList';
import { AddOnsList } from './AddOnsList';

interface ServicesTabProps {
  canEdit: boolean;
}

export const ServicesTab: React.FC<ServicesTabProps> = ({ canEdit }) => {
  return (
    <DataErrorBoundary invalidateOnRetry={['services', 'addOns', 'questionnaires']}>
      <Suspense
        fallback={
          <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
            Loading services configuration...
          </div>
        }
      >
        <ServicesTabInner canEdit={canEdit} />
      </Suspense>
    </DataErrorBoundary>
  );
};

const ServicesTabInner: React.FC<ServicesTabProps> = ({ canEdit }) => {
  const serviceOptions = useServicesAll();
  const questionnaireOptions = useQuestionnairesAll();

  const [serviceSubTab, setServiceSubTab] = useState<'services' | 'addons'>('services');

  const [servicesPage, setServicesPage] = useState(1);
  const [servicesPageSize, setServicesPageSize] = useState(25);
  const [servicesSearch, setServicesSearch] = useState('');
  const deferredServicesSearch = useDeferredValue(servicesSearch);

  const [addOnsPage, setAddOnsPage] = useState(1);
  const [addOnsPageSize, setAddOnsPageSize] = useState(25);
  const [addOnsSearch, setAddOnsSearch] = useState('');
  const deferredAddOnsSearch = useDeferredValue(addOnsSearch);

  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingAddOn, setEditingAddOn] = useState<AddOn | null>(null);

  useEffect(() => {
    setServicesPage(1);
  }, [servicesSearch]);
  useEffect(() => {
    setAddOnsPage(1);
  }, [addOnsSearch]);

  return (
    <div className="animate-fade-in">
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setServiceSubTab('services')}
          className={`pb-2 px-4 font-medium text-sm ${serviceSubTab === 'services' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-500'}`}
        >
          Main Services
        </button>
        <button
          onClick={() => setServiceSubTab('addons')}
          className={`pb-2 px-4 font-medium text-sm ${serviceSubTab === 'addons' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-500'}`}
        >
          Add-ons
        </button>
      </div>

      {editingService && (
        <ServiceEditor
          service={editingService}
          questionnaireOptions={questionnaireOptions}
          onClose={() => setEditingService(null)}
        />
      )}

      {editingAddOn && (
        <AddOnEditor
          addOn={editingAddOn}
          serviceOptions={serviceOptions}
          questionnaireOptions={questionnaireOptions}
          onClose={() => setEditingAddOn(null)}
        />
      )}

      {serviceSubTab === 'services' ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-slate-800">Services Configuration</h3>
            <div className="flex items-center gap-3">
              <Input
                className="w-56"
                placeholder="Search services..."
                value={servicesSearch}
                onChange={(e) => setServicesSearch(e.target.value)}
              />
              {canEdit && (
                <button
                  onClick={() =>
                    setEditingService({
                      id: '',
                      name: '',
                      description: '',
                      price: 0,
                      durationMinutes: 0,
                      category: 'Medical',
                      tierPrices: {}
                    })
                  }
                  className="bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-800 transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Add Service
                </button>
              )}
            </div>
          </div>
          <DataErrorBoundary invalidateOnRetry="services">
            <Suspense
              fallback={
                <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading services...
                </div>
              }
            >
              <ServicesList
                page={servicesPage}
                pageSize={servicesPageSize}
                search={deferredServicesSearch}
                canEdit={canEdit}
                questionnaireOptions={questionnaireOptions}
                onPageChange={setServicesPage}
                onPageSizeChange={(size) => {
                  setServicesPageSize(size);
                  setServicesPage(1);
                }}
                onEdit={setEditingService}
              />
            </Suspense>
          </DataErrorBoundary>
        </>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-slate-800">Service Add-ons</h3>
            <div className="flex items-center gap-3">
              <Input
                className="w-56"
                placeholder="Search add-ons..."
                value={addOnsSearch}
                onChange={(e) => setAddOnsSearch(e.target.value)}
              />
              {canEdit && (
                <button
                  onClick={() =>
                    setEditingAddOn({
                      id: '',
                      name: '',
                      description: '',
                      price: 0,
                      durationMinutes: 15,
                      compatibleServiceIds: [],
                      tierPrices: {},
                      questionnaireConfig: undefined
                    })
                  }
                  className="bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-800 transition-all shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Add Add-on
                </button>
              )}
            </div>
          </div>
          <DataErrorBoundary invalidateOnRetry="addOns">
            <Suspense
              fallback={
                <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading add-ons...
                </div>
              }
            >
              <AddOnsList
                page={addOnsPage}
                pageSize={addOnsPageSize}
                search={deferredAddOnsSearch}
                canEdit={canEdit}
                questionnaireOptions={questionnaireOptions}
                onPageChange={setAddOnsPage}
                onPageSizeChange={(size) => {
                  setAddOnsPageSize(size);
                  setAddOnsPage(1);
                }}
                onEdit={setEditingAddOn}
              />
            </Suspense>
          </DataErrorBoundary>
        </>
      )}
    </div>
  );
};
